import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { CeoPolishResultI, ceoEmailError } from 'src/app/interfaces/ceo-emails.interface';

/**
 * ⭐ POLISH — the support chat's Polish, for email text.
 *
 * Same backend service (`SupportPolishService`: same model, same prompt, same rules:
 * fix grammar and wording, never translate, never add facts) and the same UX: the
 * correction opens in a modal next to the draft and changes NOTHING until "Apply".
 *
 * ⚠️ `{{name}}` IS CHECKED ON THE WAY BACK. The model is told not to touch
 * identifiers, but if the corrected text has a different number of `{{name}}`
 * tokens the modal says so before Apply, because a lost token silently drops the
 * personal greeting for every recipient.
 */
@Component({
  selector: 'app-ceo-polish-button',
  templateUrl: './ceo-polish-button.component.html',
})
export class CeoPolishButtonComponent {
  @Input() text = '';
  /** Whose name the server may read as context (optional). */
  @Input() userId: string | null = null;
  @Input() compact = false;
  @Output() apply = new EventEmitter<string>();

  open = false;
  loading = false;
  error = '';
  source = '';
  result: CeoPolishResultI | null = null;
  copied = false;

  constructor(private api: ApiSdkService) {}

  async polish(): Promise<void> {
    const draft = (this.text || '').trim();
    if (!draft || this.loading) return;
    this.open = true;
    this.loading = true;
    this.error = '';
    this.result = null;
    this.copied = false;
    this.source = draft;
    try {
      const response = await this.api.polishCeoEmailText(draft, this.userId);
      const data: CeoPolishResultI = response?.success?.data;
      if (data?.polishedText?.trim()) this.result = data;
      else this.error = ceoEmailError(response, 'Could not polish that text.');
    } catch {
      this.error = 'Could not polish that text.';
    } finally {
      this.loading = false;
    }
  }

  get tokenWarning(): string {
    if (!this.result) return '';
    const count = (s: string) => (s.match(/\{\{\s*name\s*\}\}/gi) || []).length;
    const before = count(this.source);
    const after = count(this.result.polishedText);
    return before !== after ? `The corrected text has ${after} {{name}} where your draft had ${before}. Check it before applying.` : '';
  }

  async copy(): Promise<void> {
    if (!this.result) return;
    try {
      await navigator.clipboard.writeText(this.result.polishedText);
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    } catch {
      /* clipboard blocked */
    }
  }

  confirm(): void {
    if (!this.result) return;
    this.apply.emit(this.result.polishedText);
    this.close();
  }

  close(): void {
    this.open = false;
  }
}
